import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { Search, Plus, Edit3, Trash2, Printer, ArrowUp, ArrowDown, ArrowUpDown, Image as ImageIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const ALL = "__all__";

function SortHeader({ field, sort, setSort, children, align = "left", testId }) {
  const active = sort.field === field;
  const dir = active ? sort.dir : null;
  const onClick = () => {
    setSort((s) =>
      s.field === field ? { field, dir: s.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }
    );
  };
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 mono text-[10px] tracking-[0.2em] font-medium cursor-pointer select-none hover:bg-zinc-800 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {children}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
      </span>
    </th>
  );
}

export default function Articles() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [groupFilter, setGroupFilter] = useState(ALL);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const [loading, setLoading] = useState(true);

  const fetchData = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/articles", { params: search ? { q: search } : {} });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchData(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const remove = async (id) => {
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Artikel gelöscht.");
      fetchData(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const groupName = (id) => groups.find((g) => g.id === id)?.name || "";
  const groupColor = (id) => groups.find((g) => g.id === id)?.color || "#A3A3A3";

  const filtered = useMemo(() => {
    let list = [...items];
    if (groupFilter !== ALL) {
      if (groupFilter === "__none__") {
        list = list.filter((a) => !a.group_id);
      } else {
        list = list.filter((a) => a.group_id === groupFilter);
      }
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av, bv;
      switch (sort.field) {
        case "ek_price":
        case "vk_price":
        case "quantity":
          av = Number(a[sort.field] || 0);
          bv = Number(b[sort.field] || 0);
          break;
        case "group":
          av = groupName(a.group_id) || "zzz";
          bv = groupName(b.group_id) || "zzz";
          break;
        default:
          av = (a[sort.field] || "").toString().toLowerCase();
          bv = (b[sort.field] || "").toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [items, groupFilter, sort, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1600px] mx-auto space-y-6" data-testid="articles-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            INVENTAR · {filtered.length} POSITIONEN
          </div>
          <h1 className="font-display text-4xl uppercase">Artikel</h1>
        </div>
        <Link to="/artikel/neu" data-testid="new-article-button">
          <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-5 font-semibold tracking-wide">
            <Plus className="w-4 h-4 mr-2" /> NEUER ARTIKEL
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Name, EAN, Regal…"
            data-testid="articles-search-input"
            className="pl-10 rounded-sm border-zinc-400 h-11 focus-visible:ring-[#F97316] focus-visible:ring-2"
          />
        </div>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="rounded-sm border-zinc-400 h-11 w-56" data-testid="articles-group-filter">
            <SelectValue placeholder="Alle Gruppen" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value={ALL}>Alle Gruppen</SelectItem>
            <SelectItem value="__none__">— ohne Gruppe —</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: g.color }} />
                  {g.name}
                  <span className="text-zinc-500 mono text-[10px]">· {g.article_count}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="px-4 py-3 mono text-[10px] tracking-[0.2em] font-medium text-left" style={{ width: 50 }}>FOTO</th>
                <SortHeader field="name" sort={sort} setSort={setSort} testId="sort-name">ARTIKEL</SortHeader>
                <SortHeader field="group" sort={sort} setSort={setSort} testId="sort-group">GRUPPE</SortHeader>
                <th className="px-4 py-3 mono text-[10px] tracking-[0.2em] font-medium text-left">EAN</th>
                <th className="px-4 py-3 mono text-[10px] tracking-[0.2em] font-medium text-left">LAGERPLATZ</th>
                <SortHeader field="quantity" sort={sort} setSort={setSort} align="right" testId="sort-qty">MENGE</SortHeader>
                <SortHeader field="ek_price" sort={sort} setSort={setSort} align="right" testId="sort-ek">EK</SortHeader>
                <SortHeader field="vk_price" sort={sort} setSort={setSort} align="right" testId="sort-vk">VK</SortHeader>
                <th className="px-4 py-3 mono text-[10px] tracking-[0.2em] font-medium text-right">AKTIONEN</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-zinc-500 mono text-xs">
                    LADEN…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="text-zinc-500 mb-3">Keine Artikel gefunden.</div>
                    <Link to="/artikel/neu">
                      <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm">
                        <Plus className="w-4 h-4 mr-2" /> Ersten Artikel anlegen
                      </Button>
                    </Link>
                  </td>
                </tr>
              ) : (
                filtered.map((a) => {
                  const low = a.min_quantity > 0 && a.quantity <= a.min_quantity;
                  const unit = a.unit || "Stk";
                  return (
                    <tr
                      key={a.id}
                      data-testid={`article-row-${a.id}`}
                      className="border-t border-zinc-100 hover:bg-zinc-50"
                    >
                      <td className="px-4 py-2">
                        {a.image_base64 ? (
                          <img
                            src={a.image_base64}
                            alt={a.name}
                            className="w-10 h-10 object-cover rounded-sm border border-zinc-200"
                            data-testid={`article-img-${a.id}`}
                          />
                        ) : (
                          <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 rounded-sm flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-zinc-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/artikel/${a.id}`} className="font-medium hover:text-[#F97316]">
                          {a.name}
                        </Link>
                        {a.description && (
                          <div className="text-xs text-zinc-500 truncate max-w-xs">{a.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.group_id ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span
                              className="inline-block w-3 h-3 rounded-sm"
                              style={{ backgroundColor: groupColor(a.group_id) }}
                            />
                            {groupName(a.group_id)}
                          </span>
                        ) : (
                          <span className="text-zinc-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono-num text-xs">{a.ean || "—"}</td>
                      <td className="px-4 py-3 font-mono-num text-xs">
                        {[a.shelf_label, a.compartment, a.slot].filter(Boolean).join(" · ") || "—"}
                        {a.location_text && (
                          <div className="text-[11px] text-zinc-500 normal-case mt-0.5">{a.location_text}</div>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono-num font-semibold ${low ? "text-red-600" : ""}`}>
                        {a.quantity}
                        <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>
                        {low && <div className="text-[10px] mono uppercase">Niedrig</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num">€ {fmt(a.ek_price)}</td>
                      <td className="px-4 py-3 text-right font-mono-num">€ {fmt(a.vk_price)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/etiketten?article=${a.id}`}
                            data-testid={`label-${a.id}`}
                            className="p-2 hover:bg-zinc-100 rounded-sm"
                            title="Etikett drucken"
                          >
                            <Printer className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/artikel/${a.id}`}
                            data-testid={`edit-${a.id}`}
                            className="p-2 hover:bg-zinc-100 rounded-sm"
                            title="Bearbeiten"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Link>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                data-testid={`delete-${a.id}`}
                                className="p-2 hover:bg-red-100 hover:text-red-600 rounded-sm"
                                title="Löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Artikel löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  „{a.name}" wird unwiderruflich entfernt.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid={`confirm-delete-${a.id}`}
                                  onClick={() => remove(a.id)}
                                  className="bg-red-600 hover:bg-red-700 rounded-sm"
                                >
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
